import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootStore } from 'store/reducer';
import { TEAMS_ACTIONS, assignUserGroupToTeam } from 'store/teams';
import {
  USER_GROUPS_ACTIONS,
  createUserGroup,
  updateUserGroup,
  selectUserGroupsAreLoading,
  selectUserGroupById,
  selectNewUserGroup
} from 'store/userGroups';
import { displayAlert } from 'store/v1/ui/ui.actions';
import useModal from 'hooks/useModal';
import useEvent from 'v1/helpers/hooks/useEvent';
import { Button, Field, Form, TextInput } from 'shared';
import Modal from 'modals/layout';

type UserGroupModalProps = {
  id?: string;
};

type UserGroupFormData = {
  name: string;
};

const copy = {
  titleCreate: 'Create user group',
  titleEdit: 'Edit user group',
  cancel: 'Cancel',
  submitCreate: 'Create',
  submitEdit: 'Save changes'
};

const UserGroupModal = ({ id }: UserGroupModalProps) => {
  const dispatch = useDispatch();
  const { cancelModal, closeModal } = useModal();
  const userGroupsLoading = useSelector(selectUserGroupsAreLoading);
  const newUserGroup = useSelector(selectNewUserGroup);
  const userGroup = useSelector((state: RootStore) =>
    id ? selectUserGroupById(state, id) : null
  );

  useEvent([USER_GROUPS_ACTIONS.createUserGroup], {
    onSuccess: () => {
      newUserGroup && dispatch(assignUserGroupToTeam(newUserGroup.id));
    },
    onFailure: () => dispatch(displayAlert('error', 'An error occurred'))
  });

  useEvent(
    [TEAMS_ACTIONS.assignUserGroupToTeam, USER_GROUPS_ACTIONS.updateUserGroup],
    {
      onSuccess: () => {
        dispatch(displayAlert('success', 'Updated successfully'));
        closeModal();
      },
      onFailure: () => dispatch(displayAlert('error', 'An error occurred'))
    }
  );

  const onSubmit = (data: UserGroupFormData) => {
    id
      ? dispatch(updateUserGroup({ id, data }))
      : dispatch(createUserGroup({ ...data, isResourceRole: false }));
  };

  return (
    <Form<UserGroupFormData>
      values={id && userGroup ? userGroup : {}}
      onSubmit={onSubmit}
    >
      <Modal size="s">
        <Modal.Header
          title={id ? copy.titleEdit : copy.titleCreate}
        />
        <Modal.Content>
          <Modal.Scrollable>
            <Field label="Name" name="name" isRequired>
              <TextInput placeholder="e.g. Leadership" />
            </Field>
          </Modal.Scrollable>
          <Modal.Toolbar>
            <Button label={copy.cancel} action={cancelModal} />
            <Button
              label={id ? copy.submitEdit : copy.submitCreate}
              icon="tick"
              appearance="primary"
              hasMarginLeft
              isLoading={!!userGroupsLoading}
              action="submit"
            />
          </Modal.Toolbar>
        </Modal.Content>
      </Modal>
    </Form>
  );
};

export default UserGroupModal;
